import { DailyActiveUsersMonthlyUsage } from '../models/dailyActiveUsers/DailyActiveUsersMonthlyUsage';
import { DailyActiveUserUsage } from '../models/dailyActiveUsers/DailyActiveUserUsage';
import { CompiledDailyActiveUserConsumption } from '../models/dailyActiveUsers/CompiledDailyActiveUserConsumption';
import { BlipApi } from './BlipApi';
import { getFirstDayOfSpecifiedMonth, getLastDayOfSpecifiedMonth } from 'utils/date';
import { useSubscriptionReferenceDate } from './Billing/BillingApi';

export default class BillingDailyActiveUsersServiceApi {
  private readonly BILLING_COMMAND_DESTINATION = 'postmaster@billing.blip.ai';
  private useSubscriptionReferenceDate: boolean | undefined = undefined;

  public async getUseSubscriptionReferenceDate(): Promise<void> {
    if (this.useSubscriptionReferenceDate === undefined) {
      this.useSubscriptionReferenceDate = await useSubscriptionReferenceDate();
    }    
  }

  public async getCompiledDailyActiveUserConsumption(
    tenantId: string,
    month: string,
  ): Promise<CompiledDailyActiveUserConsumption | null> {
    await this.getUseSubscriptionReferenceDate();
    const uri = `/tenants/${tenantId}/daily-active-users/total?month=${month}&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}`;

    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: 'get',
      uri: uri,
    };

    const consolidatedDailyActiveUserConsumption = {
      metric: null,
      total: 0,
      planUsageRate: 0,
      exceededPlan: 0,
      planUsage: 0,
      availablePlan: 0,
      startDateReference: getFirstDayOfSpecifiedMonth(new Date()),
      endDateReference: getLastDayOfSpecifiedMonth(new Date()),
    };

    try {
      const { response } = await BlipApi.sendCommand(command);
      return new CompiledDailyActiveUserConsumption(response);
    } catch (e) {
      return consolidatedDailyActiveUserConsumption;
    }
  }

  public async getDailyActiveUserMonthlyUsage(
    tenantId: string,
    month: string,
    registersToTake: number,
    storageType = 1,
  ): Promise<DailyActiveUsersMonthlyUsage[] | null> {
    await this.getUseSubscriptionReferenceDate();
    let uri = `/tenants/${tenantId}/daily-active-users/monthly/by-bot?month=${month}&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}&storageType=${storageType}`;

    if (registersToTake > 0) {
      uri = uri + `&$take=${registersToTake}`;
    }
    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: 'get',
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      return items.map((item) => new DailyActiveUsersMonthlyUsage(item));
    } catch (e) {
      return new Array<DailyActiveUsersMonthlyUsage>();
    }
  }

  public async getDailyActiveUserUsage(
    tenantId: string,
    month: string,
    registersToTake: number,
    storageType = 1,
  ): Promise<DailyActiveUserUsage[] | null> {
    await this.getUseSubscriptionReferenceDate();
    let uri = `/tenants/${tenantId}/daily-active-users/daily?month=${month}&useSubscriptionReferenceDate=${this.useSubscriptionReferenceDate}&storageType=${storageType}`;

    if (registersToTake > 0) {
      uri = uri + `&$take=${registersToTake}`;
    }
    const command = {
      to: this.BILLING_COMMAND_DESTINATION,
      method: 'get',
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);
      return items.map((item) => new DailyActiveUserUsage(item));
    } catch (e) {
      return new Array<DailyActiveUserUsage>();
    }
  }
}
